import {
    BasicGet,
    BasicPostWithResult
} from './SimpleFetchCall';

import {
    AccountingPackage,
    BannerMessage,
    BasicTenantSettingsResponse,
    ConsumerTenantFeatureSwitches,
    EmailTenantsResult,
    NewDataCollectionImportModalTextResponse,
    PortalSafeDataCollectionLimitResponse,
    PortalTextResponse,
    PortalTextSectionIds,
    TenantUserMetadataSetting,
} from '../Models/Api/strongbox.financialportal';

import { FetchMethod, fetchWrapper } from '../Utils/FetchWrapper';
import { LogException, LogMessage, SeverityLevel } from '../Utils/Logging';


export async function GetTenantEmailDomains(): Promise<string[]> {
    return await BasicGet<string[]>(
        `/api/tenant/emaildomains`,
        'GetTenantEmailDomains'
    );
}

export async function GetTenantUserMetadataSettings(): Promise<TenantUserMetadataSetting[]> {
    return await BasicGet<TenantUserMetadataSetting[]>(
        `/api/tenant/usermetadatasettings`,
        'GetTenantUserMetadataSettings'
    );
}

export async function GetTenantBannerMessages(): Promise<BannerMessage[]> {
    return await BasicGet<BannerMessage[]>(
        `/api/tenant/bannermessages`,
        'GetTenantBannerMessages'
    );
}

export async function GetTenantDataCollectionLimit(): Promise<PortalSafeDataCollectionLimitResponse> {
    return {
        hasLimit: false,
    }
    /*
    return await BasicGet<PortalSafeDataCollectionLimitResponse>(
        `/api/tenant/safedatacollectionlimit`,
        'GetTenantDataCollectionLimit'
    );
    */
}

export async function GetAccountingPackageDefinitions(): Promise<AccountingPackage[]> {
    return await BasicGet<AccountingPackage[]>(
        `/api/tenant/accountingpackages`,
        'GetAccountingPackageDefinitions'
    );
}

export async function GetPortalsForEmail(email: string, externalId: string): Promise<EmailTenantsResult> {
    const result = await BasicGet<EmailTenantsResult>(
        `/api/tenant/portalsforemaildomain/${email}/${externalId}`,
        'GetPortalsForEmail'
    );
    return result;
}

export async function GetTenantTextContent(sectionIds: PortalTextSectionIds[], languageCode: string, localeCode?: string): Promise<PortalTextResponse> {
    const body = {
        languageCode,
        locale: localeCode,
        sectionIds
    }

    return await BasicPostWithResult<PortalTextResponse>(
        '/api/tenant/portaltext',
        'RetrieveTenantPortalText',
        body
    );
}

export async function GetTenantAnonymousTextContent<T>(sectionId: 'loginPage' | 'timeoutMessaging' | 'borrowerAnonymousSubmission', languageCode: string, localeCode?: string): Promise<T> {
    // needs to operate anonymous so needs to modify second parameter to fetchWrapper and can't use the basic calls
    const nameForLog = 'GetTenantAnonymousTextContent';
    LogMessage(
        nameForLog,
        SeverityLevel.Information
    );

    let url = `/api/tenant/`;

    if (sectionId === 'loginPage') {
        url += 'portallogintext';
    } else if (sectionId === 'timeoutMessaging') {
        url += 'portaltimeouttext';
    } else if (sectionId === 'borrowerAnonymousSubmission') {
        url += 'portalborroweranonymoustext';
    }

    const body = {
        'languageCode': languageCode,
        'locale': localeCode ?? '',
    }

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Post,
                body: JSON.stringify(body),
            },
            true,
            false
        );

        if (res.ok) {
            const result: T = await res.json();
            return result;
        }

        LogException(
            `${nameForLog} failed`,
            new Error(res.statusText)
        );

        throw new Error(`Unable to invoke ${nameForLog} - ${res.statusText}`);
    } catch (exception) {
        LogException(
            `${nameForLog} failed`,
            exception
        );

        console.error(`Exception executing ${nameForLog}`);
        console.error(exception);

        throw exception;
    }
}

export async function GetTenantAccountingSystemsTextContent<T>(languageCode: string, localeCode?: string): Promise<NewDataCollectionImportModalTextResponse> {
    const body = {
        'languageCode': languageCode,
        'locale': localeCode ?? '',
    }

    return await BasicPostWithResult<NewDataCollectionImportModalTextResponse>(
        `/api/tenant/portalaccountingsystemtext`,
        'GetTenantAccountingSystemsTextContent',
        body,
        undefined, // log parameters
        false      // require auth
    );
}

export async function GetBasicTenantSettings(): Promise<BasicTenantSettingsResponse> {
    // needs to operate anonymous so needs to modify second parameter to fetchWrapper and can't use the basic calls
    const nameForLog = 'GetBasicTenantSettings';
    LogMessage(
        nameForLog,
        SeverityLevel.Information
    );

    let url = `/api/tenant/basicsettings`;

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Get,
            },
            true,
            false
        );

        if (res.ok) {
            const result: BasicTenantSettingsResponse = await res.json();
            return result;
        }

        LogException(
            `${nameForLog} failed`,
            new Error(res.statusText)
        );

        throw new Error(`Unable to invoke ${nameForLog} - ${res.statusText}`);
    } catch (exception) {
        LogException(
            `${nameForLog} failed`,
            exception
        );

        console.error(`Exception executing ${nameForLog}`);
        console.error(exception);

        throw exception;
    }
}

export async function GetTenantFeatureSwitches(): Promise<ConsumerTenantFeatureSwitches> {
    return await BasicGet<ConsumerTenantFeatureSwitches>(
        `/api/tenant/featureswitches`,
        'GetTenantFeatureSwitches'
    );
}
